import React, { useCallback } from 'react';
import { StyleSheet, Text, View, Pressable, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as Haptics from 'expo-haptics';

import { useMenu } from '../navigation/MenuContext';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { ActiveChargeAttributes } from '../models/active-charges-model';

// Define root screens where hamburger should be shown
const ROOT_SCREENS = new Set([
  'ChargerSelection',
  'TransactionHistory',
  'MyChargers',
  'HostInsights',
  'PartnerPortal',
  'PartnerEarningsDashboard',
  'AdminScreen',
  'OnboardingStatus',
]);

interface HeaderProps {
  onPressLeftButton?: () => void;
  headerText?: string;
  hasLeftIcon?: boolean;
  hasRightIcon?: boolean;
  forceHamburger?: boolean;
  forceBackArrow?: boolean;
  transparent?: boolean;
}

const Header = ({
  onPressLeftButton,
  headerText,
  hasLeftIcon = true,
  hasRightIcon,
  forceHamburger = false,
  forceBackArrow = false,
  transparent = false,
}: HeaderProps) => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  
  // Try to get menu context - will be undefined if MenuProvider not available
  let menuContext: { openMenu: () => void } | undefined;
  try {
    menuContext = useMenu();
  } catch {
    // MenuProvider not available, fallback to regular navigation
  }

  // Get current route name
  const currentRouteName = useNavigationState((state) => {
    if (!state?.routes?.length) return undefined;
    return state.routes[state.index]?.name;
  });

  // Check for active charges
  const activeCharges: ActiveChargeAttributes[] | null = useSelector(
    (state: RootState) => state.user?.activeCharges
  );
  const hasActiveCharge = activeCharges && activeCharges.length > 0;

  // Determine if we should show hamburger or back arrow
  const isRootScreen = currentRouteName ? ROOT_SCREENS.has(currentRouteName) : false;
  const canGoBack = navigation.canGoBack();
  
  const shouldShowHamburger = menuContext && (forceHamburger || (!forceBackArrow && isRootScreen && !hasLeftIcon));

  // Haptic feedback helper
  const triggerHaptic = useCallback(() => {
    if (Platform.OS !== 'web') {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
  }, []);

  const handleLeftPress = useCallback(() => {
    triggerHaptic();
    
    if (shouldShowHamburger && menuContext) {
      menuContext.openMenu();
    } else if (onPressLeftButton) {
      onPressLeftButton();
    } else {
      navigation.goBack();
    }
  }, [shouldShowHamburger, menuContext, onPressLeftButton, navigation, triggerHaptic]);

  const handleRightPress = useCallback(() => {
    triggerHaptic();
    if (onPressLeftButton) {
      onPressLeftButton();
    } else {
      navigation.goBack();
    }
  }, [onPressLeftButton, navigation, triggerHaptic]);

  return (
    <View 
      style={[
        styles.headerContainer,
        transparent ? { backgroundColor: 'transparent' } : null,
      ]}
    >
      {hasLeftIcon || shouldShowHamburger ? (
        <Pressable
          onPress={handleLeftPress}
          style={({ pressed }) => [
            styles.iconContainer,
            { opacity: pressed ? 0.6 : 1 },
          ]}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          {shouldShowHamburger ? (
            <View style={styles.hamburgerContainer}>
              <View style={styles.hamburger}>
                <View style={styles.hamburgerLine} />
                <View style={styles.hamburgerLine} />
                <View style={styles.hamburgerLine} />
              </View>
              {/* Active Charge Badge */}
              {hasActiveCharge && (
                <View style={styles.badge}>
                  <View style={styles.badgeDot} />
                </View>
              )}
            </View>
          ) : (
            <MaterialIcons name="chevron-left" size={32} color="white" />
          )}
        </Pressable>
      ) : (
        <View style={styles.iconContainer} />
      )}
      
      <Text style={styles.headerText} numberOfLines={1}>
        {headerText || ''}
      </Text>

      {hasRightIcon ? (
        <Pressable
          onPress={handleRightPress}
          style={({ pressed }) => [
            styles.iconContainer,
            { opacity: pressed ? 0.6 : 1 },
          ]}
        >
          <MaterialIcons name="close" size={32} color="white" />
        </Pressable>
      ) : (
        <View style={styles.iconContainer} />
      )}
    </View>
  );
};

export default Header;

const styles = StyleSheet.create({
  headerContainer: {
    padding: 12,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerText: {
    color: 'white',
    fontSize: 18,
    alignSelf: 'center',
    fontWeight: '400',
    fontFamily: 'PlusJakartaSans-Regular',
    flex: 1,
    textAlign: 'center',
  },
  iconContainer: {
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hamburgerContainer: {
    position: 'relative',
  },
  hamburger: {
    width: 22,
    height: 18,
    justifyContent: 'space-between',
  },
  hamburgerLine: {
    width: 22,
    height: 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 1,
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -6,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#1A1D21',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: '#39BEB7',
  },
});
